import { getDeviceLocation } from '@services/DeviceLocationService'
import _ from 'lodash'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { getIsLoggedIn } from '@atredux/selectors/ihmLoginTokenInfoSelector'
import { StoreIdServiceFor3P } from '@pages/StoreIdSelectionFor3P/utils/StoreIdServiceFor3P'

interface IStoreInfo {
    storeId: string,
    name: string,
    region: string,
    storeFamily: string,
    geofenceRadiusInMeters: number,
    appInactivityThresholdInSecs: number,
    storeGeocode: {
        latitude: string,
        longitude: string,
    }
    lastSuccessfulLocationFetch: number
}
/*
 *
 * Initiate the locationCheck in every 2 minutes and below steps are executed
 *
 * Fetches device location in every 2 minutes and calculate the distance b/w store and device location
 * Updates the timeStamp of lastSuccessful location fetch
 * if distance > geofenceRadius
 * Redirects user to store selection page
 * if lastSuccessfulLocation check > 45 minutes
 * Redirects the user to store selection
 * if currentTime- AppPausedTime > AppInactivityTime
 * Redirects the user to store selection
 *
 *
*/
export class LocationCheckUtils {

    public static maxExpectedTime: number = 2700000
    public static earthRadiusInMeters: number = 6371000
    public static locationCheckTimoutInMs: number = 120000
    public static interval: NodeJS.Timer
    public static isLocationCheckActive: boolean = false

    public static locationCheck = async () => {
        let deviceLocation: any
        console.log('ATBase: Starting periodic location check')
        const storeInfo: IStoreInfo = JSON.parse(StoreIdServiceFor3P.getStoreDetail())
        if (!storeInfo) {
            console.log('ATBase: StoreInfo not set in session storage, skipping location check')
            return
        }
        try {
            deviceLocation = await getDeviceLocation()
        } catch (error) {
            console.log('ATBase: Error occurred while fetching location ')
        }
        if (deviceLocation && deviceLocation.Accuracy <= 50) {
            console.log('ATBase: Received location with accuracy <= 50m')
            storeInfo.lastSuccessfulLocationFetch = new Date().getTime()
            StoreIdServiceFor3P.setStoreDetails(storeInfo)
            if (LocationCheckUtils.userToStoreDistance(storeInfo, deviceLocation) >= storeInfo.geofenceRadiusInMeters + deviceLocation.Accuracy) {
                await StoreIdServiceFor3P.resetStoreIdServiceFor3P()
                console.log('ATBase: User is out of geofence radius of store, redirecting to store selection page')
                LocationCheckUtils.hideLoadedIframe()
                window.location.href = LocationCheckUtils.getStoreSelectionPageURI()
            }
        } else {
            console.log('ATBase: Device location is null or accuracy is > 50m, discarding the last fetched location')
            const lastSuccessfulLocationFetchAndCurrentTimeDifference: number = (new Date().getTime()) - (storeInfo.lastSuccessfulLocationFetch)
            if (lastSuccessfulLocationFetchAndCurrentTimeDifference >= LocationCheckUtils.maxExpectedTime) {
                console.log('ATBase: Not able to fetch location with accuracy <= 50 m since 45 minutes , redirecting to store selection page')
                await StoreIdServiceFor3P.resetStoreIdServiceFor3P()
                LocationCheckUtils.hideLoadedIframe()
                window.location.href = LocationCheckUtils.getStoreSelectionPageURI()
            }
        }
    }

    public static init = () => {
        if (!EnvironmentDetail.isAndroid()) {
            console.log('ATBase: This is not an Android device.Skipping location check')
            return
        }
        console.log('ATBase: Starting location check')
        LocationCheckUtils.interval = setInterval(LocationCheckUtils.locationCheck, LocationCheckUtils.locationCheckTimoutInMs)
        LocationCheckUtils.isLocationCheckActive = true
        try {
            LocationCheckUtils.registerOnResumeHandler()
            LocationCheckUtils.registerOnPauseHandler()
        } catch (error) {
            console.log('ATBase: error occurred while registering handlers', error)
        }
    }

    public static userToStoreDistance = (storeInfo: IStoreInfo, currentDeviceLocation: any): number => {
        /*
         *  Reference- https://tiny.amazon.com/n9r9e2t3/enwikiorgwikiHave
         *  degrees to radians
        */
        let storeLatitude = Number(storeInfo.storeGeocode.latitude)
        const storeLongitude = Number(storeInfo.storeGeocode.longitude)
        let deviceLatitude = currentDeviceLocation.latitude
        const deviceLongitude = currentDeviceLocation.longitude
        // Haversine formula
        const differenceLongitude = (deviceLongitude - storeLongitude) * Math.PI / 180.0
        const differenceLatitude = (deviceLatitude - storeLatitude) * Math.PI / 180.0
        storeLatitude = storeLatitude * Math.PI / 180.0
        deviceLatitude = deviceLatitude * Math.PI / 180.0
        const haversineConstant: number = Math.pow(Math.sin(differenceLatitude / 2), 2)
            + Math.cos(deviceLatitude) * Math.cos(storeLatitude)
            * Math.pow(Math.sin(differenceLongitude / 2),2)

        const haversineDistance: number = 2 * Math.asin(Math.sqrt(haversineConstant))
        return haversineDistance * LocationCheckUtils.earthRadiusInMeters
    }

    /*
     *
     * Register locationCheck on cordova onResume event
     *
     */
    public static registerOnResumeHandler () {
        let pausedAt: number
        document.addEventListener('resume', () => {
            if (getIsLoggedIn) {
                console.log('ATBase: App is resumed, starting periodic checks')
                const currentTime: number = new Date().getTime()
                if (!_.isUndefined(window.sessionStorage.getItem('appPauseAt'))) {
                    pausedAt = Number(window.sessionStorage.getItem('appPauseAt'))
                } else {
                    pausedAt = currentTime
                }
                const storeInfo: IStoreInfo = JSON.parse(StoreIdServiceFor3P.getStoreDetail())
                if (Math.abs(currentTime - pausedAt) >= storeInfo.appInactivityThresholdInSecs * 1000) {
                    StoreIdServiceFor3P.resetStoreIdServiceFor3P()
                    LocationCheckUtils.hideLoadedIframe()
                    window.location.href = LocationCheckUtils.getStoreSelectionPageURI()
                } else if (!LocationCheckUtils.isLocationCheckActive) {
                    LocationCheckUtils.isLocationCheckActive = true
                    LocationCheckUtils.interval = setInterval(LocationCheckUtils.locationCheck, LocationCheckUtils.locationCheckTimoutInMs)
                }
            } else if (LocationCheckUtils.isLocationCheckActive) {
                clearInterval(LocationCheckUtils.interval)
            }
        })
    }

    /*
     *
     * Clears locationCheck on cordova onPause event
     *
     */
    public static registerOnPauseHandler () {
        document.addEventListener('pause', () => {
            console.log('ATBase: App is paused, clearing periodic checks')
            const currentTime: number = new Date().getTime()
            window.sessionStorage.setItem('appPauseAt', String(currentTime))
            LocationCheckUtils.isLocationCheckActive = false
            clearInterval(LocationCheckUtils.interval)
        })
    }

    /*
     *
     * Hides loaded IFrame before navigating back to store selection page
     *
     */
    public static hideLoadedIframe () {
        const migratedIframe = document.getElementsByClassName('iframe-container') as
            HTMLCollectionOf<HTMLElement>
        migratedIframe.item(0).style.display = 'none'
        const seawardIframe = document.getElementsByClassName('seaward-iframe-container') as
            HTMLCollectionOf<HTMLElement>
        seawardIframe.item(0).style.display = 'none'

    }

    /*
     *
     * Returns Store Selection page URL
     *
     */
    private static getStoreSelectionPageURI (): string {
        return window.location.href.split('#')[0] + '#/store-selection'
    }
}
